import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm, Control } from 'react-hook-form';
import {
  NostroSidepanel,
  ResponseError,
  responseErrorsResolver,
  ajvResolver,
  ValidatorsProps,
  BaseTransaction,
  PermissionEnum,
  BaseellaStatus,
  usePrevious,
  SelectItemProps,
} from '@ui/src';
import { Balance } from '@ui/src/components/transactions-sidepanels/types';
import { useAppSelector, useAppDispatch } from '@store';
import useGetAccountById from '../../store/dictionaries/helpers/use-account-by-id';
import StatusesUrl from '../../types/statuses-url';
import {
  changePanelState,
  clearNostroTransactionState,
} from './store/slices/transaction';
import { setCollapseAllRows } from './store/slices/list';
import { useGetNostroTransactionValidatorsQuery } from './store/services';
import useGetPrimaryAccountsAutocompleteList from '../../store/dictionaries/helpers/use-primary-accounts-autocomplete';
import useGetPspPartnersAutocompleteList from '../../store/dictionaries/helpers/use-psp-partners-autocomplete';
import { CreateNostroTransaction } from './types';
import { clearStatusesState } from '../../store/statuses/slice';
import { CheckTransactionStatus } from '../../types/check-transaction';
import { clearSignaturesState } from '../../store/signature/slice';
import { useLazyGetTransactionsAccountBalanceQuery } from '../../store/dictionaries/services';
import { MfaToken, MfaType, PurposeCodeSettings } from '../../store/types';
import { clearExpandedGeneralLedgerTransactionState } from '../transactions/store/slice';
import useBaseRequests from '../transactions/hooks/use-base-requests';
import useRequests from './hooks/use-requests';
import usePurposeCodesList from '../../store/dictionaries/helpers/use-purpose-codes-list';

import ROUTES from '../../routes';

const defaultFormValues: any = {
  acceptedIn: '',
  acceptedOut: '',
  currencyId: '',
  documentNumber: '',
  fromPartnerPspId: '',
  info: '',
  paymentType: '',
  recipientAccountNumber: '',
  senderAccountNumber: '',
  sum: '',
  toPartnerPspId: '',
};

const formatFormDate = (date: string) => {
  const dateFormatted = new Date(date);

  dateFormatted.setHours(0);
  dateFormatted.setMinutes(0);
  dateFormatted.setSeconds(0);
  dateFormatted.setMilliseconds(0);

  return dateFormatted;
};

const NostroTransactionDetails = (): React.JSX.Element => {
  const {
    nostro: {
      transaction: {
        panelIsOpen,
        transaction,
        validators,
        glTransactions,
        integrationLogs,
        integrationLogsPagination,
      },
      list: { selectedCount },
    },
    transactions: {
      expandedGLTransaction,
      expandedGLTransactionCreditBalance,
      expandedGLTransactionDebitBalance,
      currentEmployee,
      transactionChangeLogs,
      transactionChangeLogsPagination,
      transactionComments,
      transactionCommentsPagination,
      caspList,
    },
    transactionsDictionaries: {
      currencies,
      paymentTypes,
      activePermissions,
      baseCurrency,
      currenciesList,
      overdraftEnabled,
      purposeCodesSettings,
    },
    statuses: { statuses, statusLogs },
    signatures: { signatures },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const [fromAccountBalance, setFromAccountBalance] = useState<number>();
  const [fromAccountBalanceInfo, setFromAccountBalanceInfo] =
    useState<Balance>();
  const [toAccountBalance, setToAccountBalance] = useState<number>();
  const [toAccountBalanceInfo, setToAccountBalanceInfo] = useState<Balance>();
  const [createdTransactionId, setCreatedTransactionId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [amount, setAmount] = useState<string | undefined>();
  const [statusChangeError, setStatusChangeError] = useState('');
  const [backPath, setBackPath] = useState('');
  const [filteredPaymentTypes, setFilteredPaymentTypes] = useState<
    SelectItemProps[]
  >([]);
  const [checkError, setCheckError] = useState('');
  const [statusCheckError, setStatusCheckError] = useState('');
  const [purposeCodeSettingItem, setPurposeCodeSettingItem] = useState<
    PurposeCodeSettings | undefined
  >(undefined);
  const [purposeCodeSettingSelectionType, setPurposeCodeSettingSelectionType] =
    useState('');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { transactionId } = useParams();
  const { search, state: locationState } = useLocation();

  const {
    watch,
    control,
    formState: { dirtyFields, errors },
    setError,
    reset,
    getValues,
    setValue,
    clearErrors,
  } = useForm({
    resolver: ajvResolver(
      transactionId
        ? (validators?.updateTransactionSchema as ValidatorsProps)
        : (validators?.createTransactionSchema as ValidatorsProps),
    ),
    defaultValues: {
      ...defaultFormValues,
    },
  });

  const currencyId = watch('currencyId');
  const fromPartnerPspId = watch('fromPartnerPspId');
  const toPartnerPspId = watch('toPartnerPspId');
  const senderAccountNumber = watch('senderAccountNumber');
  const recipientAccountNumber = watch('recipientAccountNumber');
  const paymentType = watch('paymentType');
  const sender = watch('senderAccountNumber');
  const recipient = watch('recipientAccountNumber');
  const transferDate = watch('transferDate');
  const acceptedOut = watch('acceptedOut');
  const acceptedIn = watch('acceptedIn');

  const fields = watch();

  const prevCurrencyId = usePrevious(currencyId);
  const prevFromPartnerPspId = usePrevious(fromPartnerPspId);
  const prevToPartnerPspId = usePrevious(toPartnerPspId);
  const prevPaymentType = usePrevious(paymentType);

  const isDirty = Object.keys(dirtyFields).length !== 0;

  useGetNostroTransactionValidatorsQuery();

  const [
    getFromAccountBalance,
    {
      isLoading: fromAccountBalanceIsLoading,
      isFetching: fromAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const [
    getToAccountBalance,
    {
      isLoading: toAccountBalanceIsLoading,
      isFetching: toAccountBalanceIsFetching,
    },
  ] = useLazyGetTransactionsAccountBalanceQuery();

  const onClearSidepanel = () => {
    dispatch(clearNostroTransactionState());
    dispatch(clearStatusesState());
    dispatch(clearSignaturesState());
  };

  const setCollapseAllRowsAction = () => {
    dispatch(setCollapseAllRows(true));
  };

  const {
    autocompleteList: toPartnerPSPs,
    onSearch: onSearchToPartnerPsp,
    isLoading: toPartnerPSPsIsLoading,
    getAutocompleteListAction: getToPartnerPspAutocompleteList,
    pspPartnerList: toPspPartnerList,
  } = useGetPspPartnersAutocompleteList(
    paymentType ? `&paymentsTypes=${paymentType}` : '',
  );

  const {
    autocompleteList: fromPartnerPSPs,
    onSearch: onSearchFromPartnerPsp,
    isLoading: fromPartnerPSPsIsLoading,
    getAutocompleteListAction: getFromPartnerPspAutocompleteList,
  } = useGetPspPartnersAutocompleteList(
    paymentType ? `&paymentsTypes=${paymentType}` : '',
  );

  const {
    autocompleteList: fromPspAccounts,
    onSearch: onSearchFromPspAccounts,
    isLoading: fromAccountsIsLoading,
    accountsList: fromPspAccountsList,
    getAutocompleteListAction: getFromPspAccounts,
    pagination: fromPspAccountsPagination,
    nextPageIsLoading: fromPspAccountsNextPageIsLoading,
    onScroll: fromPspAccountsOnScroll,
    getTransactionPspAccountByIdAction: getFromPspAccountsByIdAction,
  } = useGetPrimaryAccountsAutocompleteList(
    fromPartnerPspId ||
      locationState?.senderAccount?.pspAccount?.pspPartner?.id,
    currencyId,
  );

  const {
    autocompleteList: toPspAccounts,
    onSearch: onSearchToPspAccounts,
    isLoading: toAccountsIsLoading,
    accountsList: toPspAccountsList,
    getAutocompleteListAction: getToPspAccounts,
    pagination: toPspAccountsPagination,
    nextPageIsLoading: toPspAccountsNextPageIsLoading,
    onScroll: toPspAccountsOnScroll,
    getTransactionPspAccountByIdAction: getToPspAccountsByIdAction,
  } = useGetPrimaryAccountsAutocompleteList(
    toPartnerPspId ||
      locationState?.recipientAccount?.pspAccount?.pspPartner?.id,
    currencyId,
  );

  const {
    account: senderAccount,
    list: fromAccounts,
    pspPartnersList: fromPartnerPspList,
    setAccount: setFromAccount,
  } = useGetAccountById(locationState?.senderAccountNumber);

  const {
    account: recipientAccount,
    list: toAccounts,
    pspPartnersList: toPartnerPspList,
    setAccount: setToAccount,
  } = useGetAccountById(locationState?.recipientAccountNumber);

  const {
    autocompleteList: purposeCodes,
    onSearch: onSearchPurposeCodes,
    isLoading: purposeCodesIsLoading,
    nextPageIsLoading: purposeCodesNextPageIsLoading,
    onScroll: onScrollPurposeCodes,
    pagination: purposeCodesPagination,
    getPurposeCodeByCode,
  } = usePurposeCodesList(purposeCodeSettingItem?.purposeCodeType);

  const getFromAccountBalanceAction = useCallback(
    async (accountId?: string) => {
      let accountIdValue = '';

      if (accountId) {
        accountIdValue = accountId;
      } else {
        const currentAccount = fromPspAccountsList.find(
          (item) => item.account?.fullNumber === sender,
        );

        if (currentAccount) {
          accountIdValue = currentAccount?.account?.id as string;
        }
      }

      const response = (await getFromAccountBalance({
        id: accountIdValue,
        currencyId,
      })) as { data: { data: { edges: { node: Balance }[] } } };

      if (response?.data?.data) {
        setFromAccountBalance(response.data.data.edges[0]?.node?.value);
        setFromAccountBalanceInfo(response.data.data.edges[0]?.node);
      }
    },
    [currencyId, sender, fromPspAccountsList],
  );

  const getToAccountBalanceAction = useCallback(async () => {
    const currentAccount = toPspAccountsList.find(
      (item) => item.account?.fullNumber === recipient,
    );

    if (currentAccount) {
      const response = (await getToAccountBalance({
        id: currentAccount?.account?.id as string,
        currencyId,
      })) as { data: { data: { edges: { node: Balance }[] } } };

      if (response.data.data) {
        setToAccountBalance(response.data.data.edges[0]?.node?.value);
        setToAccountBalanceInfo(response.data.data.edges[0]?.node);
      }
    }
  }, [recipient, currencyId, toPspAccountsList]);

  const {
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    isLoading,
    isUninitialized,
    create,
    createIsLoading,
    createIsSuccess,
    editIsLoading,
    editIsError,
    getTransactionsList,
    getTransaction,
    onClearTimer,
    internalError,
    onSubmit,
    getStatusesTransactionById,
    checkPaymentAction,
    checkStatus,
    checkInProgress,
    submitDisabled,
    setSubmitDisabled,
    getIntegrationLogs,
    integrationLogsIsLoading,
    integrationLogsIsFetching,
    transferDateWithTime,
    acceptedInWithTime,
    acceptedOutWithTime,
    setTransferDateWithTime,
    setAcceptedInWithTime,
    setAcceptedOutWithTime,
  } = useRequests({
    activePermissions,
    transactionId,
    setErrorMessage,
    setStatusChangeError,
    errors,
    getValues,
    setError,
    transaction,
    setCheckError,
    fields,
    setStatusCheckError,
    clearErrors,
    toPspPartnerList,
    purposeCodeSettingItem,
  });

  const handleClose = useCallback(() => {
    dispatch(clearNostroTransactionState());
    dispatch(clearSignaturesState());

    onClearTimer(true);

    setTimeout(() => {
      if (backPath) {
        navigate(backPath);
      } else {
        navigate(`${ROUTES.NOSTRO_TRANSFERS}${search || ''}`);
      }

      setBackPath('');
    }, 100);
  }, [backPath, search]);

  const {
    deleteTransactionError,
    errorMessageSignature,
    setErrorMessageSignature,
    signaturePopupVisible,
    setSignaturePopupVisible,
    statusChangeInProgress,
    statusPopupVisible,
    setStatusPopupVisible,
    rejectPopupVisible,
    setRejectPopupVisible,
    deleteTransactionInProgress,
    debitBalanceIsLoading,
    debitBalanceIsFetching,
    creditBalanceIsLoading,
    creditBalanceIsFetching,
    glTransactionIsLoading,
    glTransactionIsFetching,
    glIsLoading,
    isLoadingSignatures,
    createSignatureInProgress,
    signatureIsSuccess,
    sendCodeInProgress,
    validateCodeInProgress,
    changeStatusIsSuccess,
    changeStatusIsLoading,
    isLoadingLogs,
    onDeleteTransaction,
    onGlTransactionClick,
    onSendCode,
    validateCodeAction,
    getLogsTransactionByIdAction,
    onClearRequestTimer,
    onChangeStatusPopup,
    onSignatureCancelClick,
    onChangeStatus,
    onReject,
    sendCodeRetry,
    sendCode,
    onClearRequestTimeout,
    changeStatusIsError,
    getGeneralLedgerTransactionValuesAction,
    getSignaturesTransactionByIdAction,
    getTransactionChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getTransactionCommentsAction,
    commentsIsFetching,
    commentsIsLoading,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
  } = useBaseRequests({
    setError,
    transactionId,
    transaction,
    currentEmployee,
    setErrorMessage,
    createdTransactionId,
    statusUrl: StatusesUrl.NOSTRO,
    setStatusChangeError,
    onSubmit,
    getTransactionsList,
    getStatusesTransactionById,
    onClearTimer,
    getTransaction,
    reset,
    handleClose,
    onClear: onClearSidepanel,
    takeCount: selectedCount,
    search,
    transactionListUrl: ROUTES.NOSTRO_TRANSFERS,
    errors,
    isDirty,
    setCollapseAllRows: setCollapseAllRowsAction,
    expandedGLTransaction,
    transactionUrl: 'nostro',
    paymentType,
  });

  const prevSignaturePopupVisible = usePrevious(signaturePopupVisible);

  const sendCodeAction = async () => {
    let createResponse;

    setErrorMessage('');

    if (!transactionId && !Object.keys(errors).length) {
      const values = getValues();

      let transferDateValue;

      if (values.transferDate) {
        transferDateValue = new Date(values.transferDate);

        if (!transferDateWithTime) {
          const dateNow = new Date();

          transferDateValue.setHours(dateNow.getHours());
          transferDateValue.setMinutes(dateNow.getMinutes());
          transferDateValue.setSeconds(dateNow.getSeconds());
        }

        const body = {
          currencyId: values.currencyId,
          sum: Number(values.sum),
          paymentType: values.paymentType,
          transferDate: transferDateWithTime
            ? transferDateValue?.toUTCString()
            : transferDateValue?.toISOString(),
          disableExternalTransactionCreate:
            values?.disableExternalTransactionCreate,
        } as CreateNostroTransaction;

        if (values.documentNumber) {
          body.documentNumber = values.documentNumber;
        }

        if (values.info) {
          body.info = values.info;
        }

        if (values.senderAccountNumber) {
          body.senderAccountNumber = values.senderAccountNumber;
        }

        if (values.recipientAccountNumber) {
          body.recipientAccountNumber = values.recipientAccountNumber;
        }

        if (values.acceptedIn) {
          if (acceptedInWithTime) {
            body.acceptedIn = new Date(values.acceptedIn).toUTCString();
          } else {
            body.acceptedIn = values.acceptedIn;
          }
        }

        if (values.acceptedOut) {
          if (acceptedOutWithTime) {
            body.acceptedOut = new Date(values.acceptedOut).toUTCString();
          } else {
            body.acceptedOut = values.acceptedOut;
          }
        }

        if (values?.caspId) {
          body.caspId = values.caspId;
        }

        if (values.purposeCode) {
          body.purposeCode = values.purposeCode;
        }

        createResponse = (await create(body)) as {
          error: ResponseError;
          data?: {
            data: BaseTransaction;
          };
        };
      } else {
        setError('transferDate', {
          type: 'custom',
          message: 'Transfer Date is required',
        });
      }

      if (createResponse && createResponse.error) {
        responseErrorsResolver({
          errors: createResponse.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else if (createResponse && createResponse.data) {
        if (createResponse && createResponse.data) {
          setCreatedTransactionId(createResponse.data.data.id);
        }

        if (currentEmployee?.canSignTransactions) {
          const response = (await sendCode({ mfa: MfaType.TOTP })) as {
            data: { data: MfaToken };
          };

          if (response && response.data && response.data.data) {
            setSignaturePopupVisible(true);

            const expireTime = Math.round(
              new Date(response.data.data.expiresAt).getTime() - Date.now(),
            );

            sendCodeRetry(expireTime);
          }
        }
      }
    }
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    switch (name) {
      case 'fromPartnerPspId':
        onSearchFromPartnerPsp(searchAutocomplete);
        break;
      case 'toPartnerPspId':
        onSearchToPartnerPsp(searchAutocomplete);
        break;
      case 'senderAccountNumber':
        onSearchFromPspAccounts(searchAutocomplete);
        break;
      case 'recipientAccountNumber':
        onSearchToPspAccounts(searchAutocomplete);
        break;
      case 'purposeCode':
        onSearchPurposeCodes(searchAutocomplete);
        break;
      default:
        break;
    }
  };

  const onDuplicate = () => {
    dispatch(clearNostroTransactionState());
    onClearRequestTimer();
    onClearTimer(true);
    reset({});

    setTimeout(() => {
      navigate(`${ROUTES.NOSTRO_TRANSFERS}${search || ''}`);
    }, 100);

    setTimeout(() => {
      navigate(`${ROUTES.NOSTRO_TRANSFER_CREATE}${search || ''}`, {
        state: { ...transaction, duplicate: true },
      });
    }, 200);
  };

  useEffect(() => {
    if (changeStatusIsError || editIsError) {
      setStatusPopupVisible(false);
    }
  }, [changeStatusIsLoading, editIsError]);

  useEffect(() => {
    if (sender && currencyId && fromPspAccountsList) {
      const currentAccount = fromPspAccountsList.find(
        (item) => item.account?.fullNumber === sender,
      );

      if (currentAccount) {
        getFromAccountBalanceAction(currentAccount?.account?.id);
      }
    }
  }, [currencyId, sender, fromPspAccountsList]);

  useEffect(() => {
    if (recipient && currencyId && toPspAccountsList) {
      getToAccountBalanceAction();
    }
  }, [recipient, currencyId, toPspAccountsList]);

  useEffect(() => {
    if (transactionId) {
      dispatch(changePanelState({ state: true, transactionId }));
    } else {
      dispatch(changePanelState({ state: true }));
    }

    return () => {
      dispatch(clearNostroTransactionState());
      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});
    };
  }, [dispatch, transactionId]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();
    }
  }, [dispatch, panelIsOpen, transactionId]);

  useEffect(() => {
    if (
      prevFromPartnerPspId &&
      prevFromPartnerPspId !== undefined &&
      prevFromPartnerPspId !== null &&
      ((fromPartnerPspId && fromPartnerPspId !== prevFromPartnerPspId) ||
        (prevFromPartnerPspId && !fromPartnerPspId)) &&
      !transactionId
    ) {
      setValue('senderAccountNumber', '');
    }
  }, [fromPartnerPspId, prevFromPartnerPspId]);

  useEffect(() => {
    if (
      prevToPartnerPspId &&
      prevToPartnerPspId !== undefined &&
      prevToPartnerPspId !== null &&
      ((toPartnerPspId && toPartnerPspId !== prevToPartnerPspId) ||
        (prevToPartnerPspId && !toPartnerPspId))
    ) {
      setValue('recipientAccountNumber', '');
    }
  }, [toPartnerPspId, prevToPartnerPspId]);

  useEffect(() => {
    if (transaction) {
      setSubmitDisabled(false);

      reset({
        ...transaction,
        fromPartnerPspId:
          transaction?.senderAccount?.pspAccount?.pspPartner?.id || '',
        toPartnerPspId:
          transaction?.recipientAccount?.pspAccount?.pspPartner?.id || '',
        sum: transaction.sum.toString(),
      });

      if (transaction.sum) {
        setAmount(transaction.sum.toString());
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction && transaction?.purposeCode && purposeCodes?.length) {
      const currentPurposeCode = purposeCodes?.find(
        (item) => item.value === transaction?.purposeCode,
      );

      if (!currentPurposeCode) {
        getPurposeCodeByCode(transaction?.purposeCode);
      }
    }
  }, [transaction, purposeCodes]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      locationState?.purposeCode &&
      purposeCodes?.length
    ) {
      const currentPurposeCode = purposeCodes?.find(
        (item) => item.value === locationState?.purposeCode,
      );

      if (!currentPurposeCode) {
        getPurposeCodeByCode(locationState?.purposeCode);
      }
    }
  }, [locationState, purposeCodes, transactionId]);

  useEffect(() => {
    if (acceptedOut) {
      const transferDateFormatted = formatFormDate(transferDate);
      const formattedAcceptedOut = formatFormDate(acceptedOut);
      const currentDate = formatFormDate(new Date().toISOString());

      if (
        (transferDate && formattedAcceptedOut < transferDateFormatted) ||
        formattedAcceptedOut > currentDate
      ) {
        setError('acceptedOut', {
          type: 'custom',
          message:
            'Accepted Out date must be between Creation Date and current date',
        });
      } else {
        clearErrors('acceptedOut');
      }
    }
  }, [acceptedOut, transferDate]);

  useEffect(() => {
    if (transferDate) {
      const formattedTransferDate = formatFormDate(transferDate);
      const formattedCurrentDate = formatFormDate(new Date().toISOString());

      if (transferDate && formattedTransferDate > formattedCurrentDate) {
        setError('transferDate', {
          type: 'custom',
          message: 'Creation Date cannot be later than current date',
        });
      } else {
        clearErrors('transferDate');
      }
    }
  }, [transferDate]);

  useEffect(() => {
    if (acceptedIn && acceptedOut) {
      const formattedAcceptedOut = formatFormDate(acceptedOut);
      const formattedAcceptedIn = formatFormDate(acceptedIn);
      const currentDate = formatFormDate(new Date().toISOString());

      if (
        formattedAcceptedIn > currentDate ||
        formattedAcceptedIn < formattedAcceptedOut
      ) {
        setError('acceptedIn', {
          type: 'custom',
          message:
            'Accepted In date must be between Accepted Out date and current date',
        });
      } else {
        clearErrors('acceptedIn');
      }
    }
  }, [acceptedIn, acceptedOut]);

  useEffect(() => {
    if (
      changeStatusIsSuccess &&
      (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
        transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
        transaction?.baseellaStatus === BaseellaStatus.REJECTED)
    ) {
      getTransactionsList(
        `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
      );
    }

    if (
      (signatureIsSuccess && !transactionId) ||
      (changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED))
    ) {
      dispatch(clearNostroTransactionState());
      dispatch(clearStatusesState());
      onClearTimer(true);
      onClearRequestTimeout();
      reset({});

      handleClose();

      if (
        changeStatusIsSuccess &&
        (transaction?.baseellaStatus === BaseellaStatus.ARCH ||
          transaction?.baseellaStatus === BaseellaStatus.AML_REJ ||
          transaction?.baseellaStatus === BaseellaStatus.REJECTED)
      ) {
        setTimeout(() => {
          getTransactionsList(
            `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
          );
        }, 100);
      }
    }
  }, [signatureIsSuccess, transactionId]);

  useEffect(() => {
    if (transactionId && signatureIsSuccess) {
      onClearTimer(true);
      onClearRequestTimeout();
      setSignaturePopupVisible(false);

      setTimeout(() => {
        getTransactionsList(
          `?take=${selectedCount || '25'}${search.replace('?', '&')}`,
        );
      }, 100);

      getTransaction();
      getSignaturesTransactionByIdAction();
    }
  }, [transactionId, signatureIsSuccess]);

  useEffect(() => {
    if (
      currencyId &&
      paymentType &&
      !transactionId &&
      !locationState?.duplicate
    ) {
      reset({
        ...getValues(),
        fromPartnerPspId: undefined,
        toPartnerPspId: undefined,
        senderAccountNumber: undefined,
        recipientAccountNumber: undefined,
      });
    }
  }, [currencyId, paymentType, transactionId, locationState]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      locationState &&
      locationState.duplicate &&
      currencyId !== prevCurrencyId
    ) {
      setValue('senderAccountNumber', '');
      setValue('recipientAccountNumber', '');
      getFromPspAccounts(currencyId);
      getToPspAccounts(currencyId);
    }
  }, [currencyId, prevCurrencyId, locationState]);

  useEffect(() => {
    if (transactionId || (locationState && locationState?.duplicate)) {
      if (senderAccount && senderAccount.pspAccount) {
        setValue('fromPartnerPspId', senderAccount.pspAccount.pspPartnerId);
      }
      if (recipientAccount && recipientAccount.pspAccount) {
        setValue('toPartnerPspId', recipientAccount.pspAccount.pspPartnerId);
      }
    }
  }, [transactionId, senderAccount, recipientAccount, locationState]);

  useEffect(() => {
    if (
      !transactionId &&
      ((paymentType && !prevPaymentType) ||
        (paymentType && prevPaymentType && paymentType !== prevPaymentType))
    ) {
      getFromPartnerPspAutocompleteList();
      getToPartnerPspAutocompleteList();
    }
  }, [paymentType, prevPaymentType, transactionId]);

  useEffect(() => {
    if (transactionId && transaction && paymentType) {
      getFromPartnerPspAutocompleteList();
    }
  }, [transaction, transactionId, paymentType]);

  useEffect(() => {
    if (transactionId && transaction && paymentType) {
      getToPartnerPspAutocompleteList();
    }
  }, [transaction, transactionId, paymentType]);

  useEffect(() => {
    if (!currencyId && baseCurrency) {
      reset({
        ...fields,
        currencyId: baseCurrency?.id,
      });
    }
  }, [baseCurrency, currencyId, locationState]);

  useEffect(() => {
    if (purposeCodesSettings) {
      if (
        purposeCodeSettingSelectionType === 'country' &&
        prevToPartnerPspId !== null &&
        toPartnerPspId &&
        prevToPartnerPspId !== toPartnerPspId
      ) {
        if (!transactionId) {
          setValue('purposeCode', '');
          setPurposeCodeSettingItem(undefined);
        }

        const currentPspPartner = toPspPartnerList?.find(
          (item) => item.id === toPartnerPspId,
        );

        if (currentPspPartner) {
          let currentPurposeCodeSettings = purposeCodesSettings?.filter(
            (item) => item.bankCountry === currentPspPartner.countryId,
          );

          if (recipientAccountNumber) {
            const currentRecipientAccount = toPspAccountsList?.find(
              (item) => item.account?.fullNumber === recipientAccountNumber,
            );

            if (
              currentRecipientAccount &&
              currentRecipientAccount?.useOwnBankDetails
            ) {
              currentPurposeCodeSettings = purposeCodesSettings?.filter(
                (item) =>
                  item.bankCountry === currentRecipientAccount.bankCountryId,
              );
            }
          }

          if (currentPurposeCodeSettings?.length) {
            setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
          }
        }
      }

      if (
        purposeCodeSettingSelectionType === 'currency' &&
        ((currencyId && prevCurrencyId && currencyId !== prevCurrencyId) ||
          (paymentType &&
            prevPaymentType !== null &&
            prevPaymentType !== paymentType))
      ) {
        if (!transactionId) {
          setValue('purposeCode', '');
          setPurposeCodeSettingItem(undefined);
        }

        const currentPaymentType = paymentTypes?.find(
          (item) => item.paymentType === fields?.paymentType,
        );

        const currentPurposeCodeSettings = purposeCodesSettings?.filter(
          (item) =>
            item.paymentCategoryId === currentPaymentType?.id &&
            item.currenciesIds.includes(fields.currencyId),
        );

        if (currentPurposeCodeSettings?.length) {
          setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
        }
      }
    }
  }, [
    currencyId,
    prevCurrencyId,
    paymentType,
    prevPaymentType,
    toPartnerPspId,
    prevToPartnerPspId,
    purposeCodeSettingSelectionType,
    purposeCodesSettings,
    transactionId,
    recipientAccountNumber,
  ]);

  useEffect(() => {
    if (
      fields.purposeCode &&
      !purposeCodeSettingItem &&
      !transactionId &&
      !locationState.duplicate
    ) {
      setValue('purposeCode', '');
    }
  }, [fields.purposeCode, purposeCodeSettingItem, transactionId]);

  useEffect(() => {
    if (purposeCodesSettings) {
      if (
        ((fields?.currencyId && fields?.paymentType) ||
          fields?.toPartnerPspId) &&
        paymentTypes &&
        !purposeCodeSettingItem
      ) {
        if (fields?.currencyId && fields?.paymentType) {
          const currentPaymentType = paymentTypes?.find(
            (item) => item.paymentType === fields?.paymentType,
          );

          const currentPurposeCodeSettings = purposeCodesSettings?.filter(
            (item) =>
              item.paymentCategoryId === currentPaymentType?.id &&
              item.currenciesIds.includes(fields.currencyId),
          );

          if (currentPurposeCodeSettings?.length) {
            setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
            setPurposeCodeSettingSelectionType('currency');
          }
        }

        if (fields.toPartnerPspId) {
          const currentPspPartner = toPspPartnerList?.find(
            (item) => item.id === fields.toPartnerPspId,
          );

          if (currentPspPartner) {
            let currentPurposeCodeSettings = purposeCodesSettings?.filter(
              (item) => item.bankCountry === currentPspPartner.countryId,
            );

            if (recipientAccountNumber) {
              const currentRecipientAccount = toPspAccountsList?.find(
                (item) => item.account?.fullNumber === recipientAccountNumber,
              );

              if (
                currentRecipientAccount &&
                currentRecipientAccount?.useOwnBankDetails
              ) {
                currentPurposeCodeSettings = purposeCodesSettings?.filter(
                  (item) =>
                    item.bankCountry === currentRecipientAccount.bankCountryId,
                );
              }
            }

            if (currentPurposeCodeSettings?.length) {
              setPurposeCodeSettingItem(currentPurposeCodeSettings[0]);
              setPurposeCodeSettingSelectionType('country');
            }
          }
        }
      }
    }
  }, [
    fields?.currencyId,
    fields?.paymentType,
    paymentTypes,
    fields?.toPartnerPspId,
    recipientAccountNumber,
    toPspAccountsList,
  ]);

  useEffect(() => {
    if (
      isDirty &&
      fields.paymentType &&
      fields.sum.toString() &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      fields.currencyId &&
      transaction?.baseellaStatus !== BaseellaStatus.ARCH &&
      transaction?.baseellaStatus !== BaseellaStatus.AML_REJ &&
      transaction?.baseellaStatus !== BaseellaStatus.REJECTED &&
      fields.info &&
      !fromAccountBalanceIsLoading &&
      !fromAccountBalanceIsFetching
    ) {
      if (fields.paymentType !== 'CRYPTO_ASSET_PAYMENT') {
        if (!purposeCodeSettingItem) {
          checkPaymentAction();
        } else if (purposeCodeSettingItem) {
          if (
            purposeCodeSettingSelectionType === 'currency' &&
            fields.purposeCode
          ) {
            checkPaymentAction();
          } else if (
            purposeCodeSettingSelectionType === 'country' &&
            fields.toPartnerPspId &&
            fields.purposeCode
          ) {
            const currentPspPartner = toPspPartnerList?.find(
              (item) => item.id === fields.toPartnerPspId,
            );

            if (currentPspPartner && currentPspPartner.countryId) {
              checkPaymentAction();
            }
          }
        }
      } else if (fields.paymentType === 'CRYPTO_ASSET_PAYMENT') {
        if (fields?.caspId) {
          checkPaymentAction();
        }
      }
    }
  }, [
    isDirty,
    checkPaymentAction,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    transaction?.baseellaStatus,
    fields.info,
    fields?.caspId,
    fromAccountBalanceIsFetching,
    fromAccountBalanceIsLoading,
    purposeCodeSettingItem,
    fields.purposeCode,
    fields.toPartnerPspId,
    purposeCodeSettingSelectionType,
  ]);

  useEffect(() => {
    if (
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      !transactionId &&
      !locationState
    ) {
      setValue('paymentType', '');
      setValue('caspId', '');
      setSubmitDisabled(true);
    }
  }, [currencyId, prevCurrencyId, locationState]);

  useEffect(() => {
    if (
      locationState &&
      locationState.duplicate &&
      currencyId &&
      prevCurrencyId &&
      currencyId !== prevCurrencyId &&
      paymentType &&
      paymentTypes
    ) {
      const nextPaymentTypes: SelectItemProps[] = [];

      paymentTypes
        ?.filter((item) => item.enabled)
        ?.forEach((item) => {
          if (
            item?.currencies?.edges?.find(
              (currency) => currency.node.id === currencyId,
            )
          ) {
            nextPaymentTypes.push({
              name: item.name,
              value: item.paymentType,
            });
          }
        });

      if (!nextPaymentTypes?.find((item) => item.value === paymentType)) {
        setValue('paymentType', '');
      }
    }
  }, [locationState, paymentType, currencyId, prevCurrencyId, paymentTypes]);

  useEffect(() => {
    if (transactionId && isDirty) {
      onClearTimer();
    }
  }, [isDirty, transactionId]);

  let buttonText: string;

  if (createIsLoading || editIsLoading) {
    buttonText = 'Loading...';
  } else {
    buttonText = transactionId ? 'Save' : 'Add';
  }

  const isLoadingSidepanel = useMemo(() => {
    let res = true;

    let isLoadingSidepanelVariables = !isLoading && !isUninitialized;

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_NOSTRO_EDIT_ACTION,
      )
    ) {
      isLoadingSidepanelVariables =
        isLoadingSidepanelVariables &&
        !isLoadingStatuses &&
        !isUninitializedStatuses;
    }

    if (transactionId) {
      if (isLoadingSidepanelVariables) {
        res = false;
      }
    } else {
      res = false;
    }

    return res;
  }, [
    transactionId,
    isLoading,
    isLoadingStatuses,
    isUninitialized,
    isUninitializedStatuses,
    activePermissions,
  ]);

  const filteredToAccounts = useMemo(() => {
    if (transactionId) {
      return toAccounts.filter(
        (item) => item.value !== fields.senderAccountNumber,
      );
    }
    return toPspAccounts.filter(
      (item) => item.value !== fields.senderAccountNumber,
    );
  }, [toAccounts, fields.senderAccountNumber]);

  const filteredFromAccounts = useMemo(() => {
    if (
      transactionId &&
      transaction?.baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS
    ) {
      return fromAccounts.filter(
        (item) => item.value !== fields.recipientAccountNumber,
      );
    }

    return fromPspAccounts.filter(
      (item) => item.value !== fields.recipientAccountNumber,
    );
  }, [fromAccounts, fields.recipientAccountNumber, transaction, transactionId]);

  useEffect(() => {
    if (
      transaction &&
      transaction?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
      fromPartnerPspId
    ) {
      getFromPspAccounts();
    }
  }, [transaction, fromPartnerPspId]);

  useEffect(() => {
    if (transaction) {
      if (transaction.senderAccount) {
        setFromAccount(transaction.senderAccount);
      }

      if (transaction.recipientAccount) {
        setToAccount(transaction.recipientAccount);
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (!prevSignaturePopupVisible && errorMessageSignature) {
      setErrorMessageSignature('');
    }
  }, [prevSignaturePopupVisible, errorMessageSignature]);

  useEffect(() => {
    if (locationState && !locationState.duplicate) {
      setBackPath(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (transaction && transaction?.senderAccount && fromPspAccounts?.length) {
      const currentAccount = fromPspAccounts?.find(
        (item) => item.value === transaction?.senderAccountNumber,
      );

      if (!currentAccount) {
        getFromPspAccountsByIdAction(transaction?.senderAccountNumber);
      }
    }
  }, [transaction, fromPspAccounts]);

  useEffect(() => {
    if (transaction && transaction?.senderAccount && toPspAccounts?.length) {
      const currentAccount = toPspAccounts?.find(
        (item) => item.value === transaction?.recipientAccountNumber,
      );

      if (!currentAccount) {
        getToPspAccountsByIdAction(transaction?.recipientAccountNumber);
      }
    }
  }, [transaction, toPspAccounts]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      senderAccountNumber &&
      fromPspAccounts
    ) {
      const currentAccount = fromPspAccounts?.find(
        (item) => item.value === senderAccountNumber,
      );

      if (!currentAccount) {
        getFromPspAccountsByIdAction(senderAccountNumber);
      }
    }
  }, [locationState, fromPspAccounts, senderAccountNumber]);

  useEffect(() => {
    if (
      !transactionId &&
      locationState &&
      locationState.duplicate &&
      recipientAccountNumber &&
      toPspAccounts
    ) {
      const currentAccount = toPspAccounts?.find(
        (item) => item.value === recipientAccountNumber,
      );

      if (!currentAccount) {
        getToPspAccountsByIdAction(recipientAccountNumber);
      }
    }
  }, [locationState, toPspAccounts, recipientAccountNumber]);

  useEffect(() => {
    if (currencyId && paymentTypes) {
      const nextPaymentTypes: SelectItemProps[] = [];

      if (transactionId) {
        paymentTypes?.forEach((item) => {
          if (
            item?.currencies?.edges?.find(
              (currency) => currency.node.id === currencyId,
            )
          ) {
            nextPaymentTypes.push({
              name: item.name,
              value: item.paymentType,
            });
          }
        });
      } else {
        paymentTypes
          ?.filter((item) => item.enabled)
          ?.forEach((item) => {
            if (
              item?.currencies?.edges?.find(
                (currency) => currency.node.id === currencyId,
              )
            ) {
              nextPaymentTypes.push({
                name: item.name,
                value: item.paymentType,
              });
            }
          });
      }

      setFilteredPaymentTypes(nextPaymentTypes);
    }
  }, [currencyId, paymentTypes]);

  useEffect(() => {
    if (locationState && locationState.duplicate) {
      if (!fields.fromPartnerPspId) {
        onSearchAutocomplete(
          'fromPartnerPspId',
          locationState.fromPartnerPspId,
        );
      }
      if (!fields.senderAccountNumber) {
        onSearchAutocomplete(
          'senderAccountNumber',
          locationState.senderAccountNumber,
        );
      }
      if (!fields.toPartnerPspId) {
        onSearchAutocomplete('toPartnerPspId', locationState.toPartnerPspId);
      }
      if (!fields.recipientAccountNumber) {
        onSearchAutocomplete(
          'recipientAccountNumber',
          locationState.recipientAccountNumber,
        );
      }

      reset({
        sum: locationState?.sum?.toString(),
        currencyId: locationState.currencyId,
        paymentType: locationState.paymentType,
        fromPartnerPspId: locationState.fromPartnerPspId,
        senderAccountNumber: locationState.senderAccountNumber,
        toPartnerPspId: locationState.toPartnerPspId,
        recipientAccountNumber: locationState.recipientAccountNumber,
        documentNumber: locationState.documentNumber,
        info: locationState.info,
        caspId: locationState.caspId || '',
        purposeCode: locationState?.purposeCode || '',
      });

      setAmount(locationState.sum.toString());
    }
  }, [locationState]);

  useEffect(() => {
    if (
      currentEmployee &&
      !currentEmployee.canSignTransactions &&
      createIsSuccess
    ) {
      handleClose();
    }
  }, [currentEmployee, createIsSuccess]);

  const onSendCodeAction = !transactionId ? sendCodeAction : onSendCode;

  return (
    <NostroSidepanel
      enableEdit
      sidepanelExpandedKey="nostro-transaction"
      isLoading={isLoadingSidepanel || Boolean(!transaction && transactionId)}
      panelIsOpen={panelIsOpen}
      handleClose={() => handleClose()}
      title={transactionId ? 'Nostro Transfer' : 'New Nostro Transfer'}
      okButtonText={buttonText}
      control={control as Control<any>}
      handleSubmit={onSubmit}
      statusesInfo={transactionId ? statuses : undefined}
      onRollbackStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_NOSTRO_REVERSE_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onNextStatus={
        activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_NOSTRO_EDIT_ACTION,
        )
          ? (status) => onChangeStatus(status)
          : undefined
      }
      onChangeStatusPopup={onChangeStatusPopup}
      setStatusPopupVisible={setStatusPopupVisible}
      statusPopupVisible={statusPopupVisible}
      isDisabledSaveButton={
        !isDirty ||
        createIsLoading ||
        editIsLoading ||
        submitDisabled ||
        sendCodeInProgress
      }
      transactionInfo={transactionId ? transaction : undefined}
      currencies={currencies}
      paymentsTypes={filteredPaymentTypes}
      fromAccounts={filteredFromAccounts.sort(
        (a, b) =>
          Number((a.value as string).replaceAll('.', '')) -
          Number((b.value as string).replaceAll('.', '')),
      )}
      toAccounts={filteredToAccounts.sort(
        (a, b) =>
          Number((a.value as string).replaceAll('.', '')) -
          Number((b.value as string).replaceAll('.', '')),
      )}
      toPartnerPSPs={transactionId ? toPartnerPspList : toPartnerPSPs}
      fromPartnerPSPs={
        transactionId &&
        transaction?.baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS
          ? fromPartnerPspList
          : fromPartnerPSPs
      }
      paymentTypeIsFilled={Boolean(paymentType) && Boolean(currencyId)}
      toPartnerPspIsFilled={Boolean(toPartnerPspId)}
      fromPartnerPspIsFilled={Boolean(fromPartnerPspId)}
      errors={errors && Object.keys(errors).length ? errors : undefined}
      toAccountsIsLoading={toAccountsIsLoading}
      fromAccountsIsLoading={fromAccountsIsLoading}
      toPartnerPSPsIsLoading={toPartnerPSPsIsLoading}
      fromPartnerPSPsIsLoading={fromPartnerPSPsIsLoading}
      onSearchAutocomplete={onSearchAutocomplete}
      baseellaStatus={transaction?.baseellaStatus}
      changeLogs={transactionChangeLogs}
      isEditable={
        transactionId
          ? activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_NOSTRO_EDIT_ACTION,
            )
          : activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_NOSTRO_CREATE_ACTION,
            )
      }
      createAllowed={activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_NOSTRO_CREATE_ACTION,
      )}
      logs={statusLogs}
      isCreateForm={!transactionId}
      checkError={
        statusCheckError ||
        (checkStatus === CheckTransactionStatus.RED
          ? 'Transaction could not be completed'
          : undefined)
      }
      currencyId={currencyId}
      updateFromAccountBalance={getFromAccountBalanceAction}
      fromAccountBalance={fromAccountBalance}
      balanceFromAccountIsLoading={
        fromAccountBalanceIsLoading || fromAccountBalanceIsFetching
      }
      toAccountBalance={toAccountBalance}
      updateToAccountBalance={getToAccountBalanceAction}
      balanceToAccountIsLoading={
        toAccountBalanceIsLoading || toAccountBalanceIsFetching
      }
      internalError={internalError}
      isDirty={isDirty}
      checkInProgress={checkInProgress}
      transactionCheckError={checkError}
      signaturePopupVisible={signaturePopupVisible}
      sendCode={onSendCodeAction}
      validateCode={validateCodeAction}
      transactionId={transactionId}
      validateCodeInProgress={
        validateCodeInProgress ||
        sendCodeInProgress ||
        createSignatureInProgress
      }
      signatures={signatures}
      errorSidepanel={errorMessage}
      errorMessageSignature={errorMessageSignature}
      rejectPopupVisible={rejectPopupVisible}
      setRejectPopupVisible={setRejectPopupVisible}
      onReject={onReject}
      rejectInProgress={changeStatusIsLoading}
      updateStatusIsLoading={statusChangeInProgress}
      isLoadingStatuses={isFetchingStatuses}
      onSignatureCancelClick={onSignatureCancelClick}
      activePermission={activePermissions}
      amount={amount}
      createdByOrganizationId={transaction?.createdByOrganizationId}
      setAmount={setAmount}
      latestQueuedStatus={transaction?.latestQueuedStatuses?.baseellaStatus}
      statusChangeError={statusChangeError}
      onChangeStatusPopupLoading={changeStatusIsLoading || editIsLoading}
      glTransactionsIsLoading={glIsLoading}
      glTransactionIsLoading={glTransactionIsLoading || glTransactionIsFetching}
      glTransactions={glTransactions}
      onGlTransactionClick={onGlTransactionClick}
      glTransaction={expandedGLTransaction}
      clearGlTransactionState={() =>
        dispatch(clearExpandedGeneralLedgerTransactionState())
      }
      expandedGLTransactionCreditBalance={expandedGLTransactionCreditBalance}
      expandedGLTransactionDebitBalance={expandedGLTransactionDebitBalance}
      expendedDebitBalanceIsLoading={
        debitBalanceIsLoading || debitBalanceIsFetching
      }
      expendedCreditBalanceIsLoading={
        creditBalanceIsLoading || creditBalanceIsFetching
      }
      deleteTransactionError={deleteTransactionError}
      onDeleteTransactions={onDeleteTransaction}
      deleteTransactionInProgress={deleteTransactionInProgress}
      updateIsLoading={editIsLoading}
      createIsLoading={createIsLoading}
      canSignTransaction={currentEmployee?.canSignTransactions}
      deleteTransactionDisabled={
        !activePermissions?.includes(
          PermissionEnum.TRANSACTIONS_GENERAL_LEDGER_DELETE_ACTION,
        )
      }
      sendCodeInProgress={sendCodeInProgress}
      currenciesList={currenciesList}
      onDuplicate={onDuplicate}
      duplicate={locationState?.duplicate}
      fields={fields}
      isLoadingLogs={isLoadingLogs}
      getLogsTransactionByIdAction={getLogsTransactionByIdAction}
      getGeneralLedgerTransactionValues={
        getGeneralLedgerTransactionValuesAction
      }
      getSignaturesTransactionById={getSignaturesTransactionByIdAction}
      isLoadingSignatures={isLoadingSignatures}
      overdraftEnabled={overdraftEnabled}
      fromPspAccountsList={fromPspAccountsList}
      toPspAccountsList={toPspAccountsList}
      fromAccountBalanceInfo={fromAccountBalanceInfo}
      toAccountBalanceInfo={toAccountBalanceInfo}
      getTransactionChangeLogs={getTransactionChangeLogsAction}
      changeLogsIsLoading={changeLogsIsLoading}
      users={users}
      transactionChangeLogsPagination={transactionChangeLogsPagination}
      changeLogsIsFetching={changeLogsIsFetching}
      transactionComments={transactionComments}
      transactionCommentsPagination={transactionCommentsPagination}
      getTransactionCommentsAction={getTransactionCommentsAction}
      commentsIsLoading={commentsIsLoading}
      commentsIsFetching={commentsIsFetching}
      onAddCommentToTransaction={onAddCommentToTransaction}
      addCommentInProgress={addCommentInProgress}
      addCommentIsSuccess={addCommentIsSuccess}
      onEditCommentInTransaction={onEditCommentInTransaction}
      editTransactionCommentInProgress={editTransactionCommentInProgress}
      editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
      onDeleteCommentInTransaction={onDeleteCommentInTransaction}
      deleteCommentInTransactionInProgress={
        deleteCommentInTransactionInProgress
      }
      deleteCommentInTranscationIsSuccess={deleteCommentInTranscationIsSuccess}
      caspList={caspList}
      paymentType={paymentType}
      getIntegrationLogs={getIntegrationLogs}
      integrationLogsIsLoading={integrationLogsIsLoading}
      integrationLogsIsFetching={integrationLogsIsFetching}
      integrationLogs={integrationLogs}
      integrationLogsPagination={integrationLogsPagination}
      fromPspAccountsPagination={fromPspAccountsPagination}
      fromPspAccountsNextPageIsLoading={fromPspAccountsNextPageIsLoading}
      fromPspAccountsOnScroll={fromPspAccountsOnScroll}
      toPspAccountsPagination={toPspAccountsPagination}
      toPspAccountsNextPageIsLoading={toPspAccountsNextPageIsLoading}
      toPspAccountsOnScroll={toPspAccountsOnScroll}
      setValue={setValue}
      purposeCodeVisible={Boolean(
        activePermissions?.includes(
          PermissionEnum.PURPOSE_CODE_SETTINGS_LIST,
        ) && purposeCodeSettingItem,
      )}
      purposeCodes={purposeCodes}
      purposeCodesIsLoading={purposeCodesIsLoading}
      purposeCodesNextPageIsLoading={purposeCodesNextPageIsLoading}
      onScrollPurposeCodes={onScrollPurposeCodes}
      purposeCodesPagination={purposeCodesPagination}
      clearErrors={clearErrors}
      deleteDisabled={ffSettings?.ffDisableTxGlRemoval}
      withTime={{
        transferDateWithTime,
        acceptedInWithTime,
        acceptedOutWithTime,
      }}
      setWithTime={{
        setTransferDateWithTime,
        setAcceptedInWithTime,
        setAcceptedOutWithTime,
      }}
    />
  );
};

export default NostroTransactionDetails;
